'use client';

import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, X, FileInput, Check, Search, List, Hash, Mail, Link as LinkIcon, AtSign, Hash as HashIcon } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { textExtractorContent } from '../../data/text-extractor-content';
import { useToast } from '@/hooks/use-toast';

type ExtractionType = 'emails' | 'urls' | 'hashtags' | 'mentions' | 'numbers' | 'custom';

export default function TextExtractor() {
    const [inputText, setInputText] = useState('');
    const [outputText, setOutputText] = useState('');
    const [extractionType, setExtractionType] = useState<ExtractionType>('emails');
    const [customPattern, setCustomPattern] = useState('');
    const [includeEmptyLines, setIncludeEmptyLines] = useState(false);
    const [sortResults, setSortResults] = useState(false);
    const [makeUnique, setMakeUnique] = useState(true);
    const { toast } = useToast();

    const extractionPatterns = {
        emails: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
        urls: /(https?:\/\/[^\s]+)|(www\.[^\s]+\.[^\s]+)/g,
        hashtags: /#\w+/g,
        mentions: /@\w+/g,
        numbers: /\b\d+\b/g,
        custom: customPattern ? new RegExp(customPattern, 'g') : /(?:)/g, // fallback empty regex
    };

    const extractText = useCallback(() => {
        if (!inputText.trim()) {
            setOutputText('');
            return;
        }

        try {
            const pattern = extractionPatterns[extractionType];
            if (!pattern) {
                setOutputText('Please enter a valid custom pattern');
                return;
            }

            const matches: string[] = [];
            let match;

            const regex = new RegExp(pattern); // reset lastIndex
            while ((match = regex.exec(inputText)) !== null) {
                if (match[0]) matches.push(match[0]);
                if (match.index === regex.lastIndex) regex.lastIndex++; // avoid zero-length loop
            }

            if (matches.length === 0) {
                setOutputText('No matches found.');
                return;
            }

            let processedMatches = [...matches];
            if (makeUnique) processedMatches = [...new Set(processedMatches)];
            if (sortResults) processedMatches.sort();

            let result = processedMatches.join('\n');
            if (!includeEmptyLines) {
                result = result.split('\n').filter((line) => line.trim() !== '').join('\n');
            }

            setOutputText(result);
        } catch (error) {
            setOutputText('Error in extraction pattern. Please check your custom regex.');
            console.error('Extraction error:', error);
        }
    }, [inputText, extractionType, customPattern, makeUnique, sortResults, includeEmptyLines]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInputText(content);
        };
        reader.readAsText(file);
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast({
            title: 'Copied!',
            description: 'Text has been copied to clipboard.',
        });
    };

    const clearText = () => {
        setInputText('');
        setOutputText('');
    };

    return (
        <div className="container mx-auto px-4 py-8">
            {/* Page Title */}
            <div className="mb-8">
                <ToolPageIntro title="Text Extractor">
                Extract text from documents and web pages with our free online tool. Remove HTML tags and extract clean text from various file formats.
            </ToolPageIntro>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Input Section */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <Label htmlFor="input-text">Input Text</Label>
                        <div className="flex items-center space-x-2">
                            {inputText && (
                                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(inputText)}>
                                    <Copy className="h-4 w-4 mr-1" /> Copy
                                </Button>
                            )}
                            {inputText && (
                                <Button variant="ghost" size="sm" onClick={clearText}>
                                    <X className="h-4 w-4 mr-1" /> Clear
                                </Button>
                            )}
                            <div className="relative">
                                <input
                                    type="file"
                                    id="file-upload"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    accept=".txt,.md,.js,.jsx,.ts,.tsx,.json,.html,.css,.scss"
                                />
                                <Label
                                    htmlFor="file-upload"
                                    className="text-sm text-muted-foreground hover:text-foreground cursor-pointer inline-flex items-center px-3 py-1.5 rounded-md hover:bg-accent transition-colors"
                                >
                                    <FileInput className="h-4 w-4 mr-2" />
                                    Upload File
                                </Label>
                            </div>
                        </div>
                    </div>
                    <Textarea
                        id="input-text"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder="Paste your text here or upload a file..."
                        className="min-h-[300px] font-mono text-sm"
                    />
                    <div className="text-sm text-muted-foreground text-right">
                        {inputText.split(/\s+/).filter(Boolean).length} words • {inputText.length} chars • {inputText.split('\n').length} lines
                    </div>
                </div>

                {/* Output Section */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <Label htmlFor="output-text">Extracted Content</Label>
                        {outputText && (
                            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(outputText)}>
                                <Copy className="h-4 w-4 mr-1" /> Copy
                            </Button>
                        )}
                    </div>
                    <div className="relative">
                        <Textarea
                            id="output-text"
                            value={outputText}
                            readOnly
                            placeholder="Extracted content will appear here..."
                            className="min-h-[300px] font-mono text-sm bg-muted/50"
                        />
                        {outputText && (
                            <div className="absolute top-2 right-2 bg-background/80 backdrop-blur-sm px-2 py-1 rounded-md text-xs text-muted-foreground">
                                {outputText.split('\n').filter(Boolean).length} items • {outputText.length} chars
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Extraction Options */}
            <Card className="mt-8">
                <CardHeader>
                    <CardTitle>Extraction Options</CardTitle>
                    <CardDescription>Choose what type of content you want to extract</CardDescription>
                </CardHeader>
                <CardContent>
                    <Tabs value={extractionType} onValueChange={(v) => setExtractionType(v as ExtractionType)}>
                        <TabsList className="grid w-full grid-cols-2 md:grid-cols-3 lg:grid-cols-6 mb-6">
                            <TabsTrigger value="emails" className="flex items-center"><Mail className="h-4 w-4 mr-2" /> Emails</TabsTrigger>
                            <TabsTrigger value="urls" className="flex items-center"><LinkIcon className="h-4 w-4 mr-2" /> URLs</TabsTrigger>
                            <TabsTrigger value="hashtags" className="flex items-center"><HashIcon className="h-4 w-4 mr-2" /> Hashtags</TabsTrigger>
                            <TabsTrigger value="mentions" className="flex items-center"><AtSign className="h-4 w-4 mr-2" /> Mentions</TabsTrigger>
                            <TabsTrigger value="numbers" className="flex items-center"><Hash className="h-4 w-4 mr-2" /> Numbers</TabsTrigger>
                            <TabsTrigger value="custom" className="flex items-center"><Search className="h-4 w-4 mr-2" /> Custom</TabsTrigger>
                        </TabsList>

                        <TabsContent value={extractionType} className="mt-0">
                            {extractionType === 'custom' && (
                                <div className="space-y-2 mb-4">
                                    <Label htmlFor="custom-pattern">Custom Regex Pattern</Label>
                                    <div className="flex space-x-2">
                                        <input
                                            id="custom-pattern"
                                            type="text"
                                            value={customPattern}
                                            onChange={(e) => setCustomPattern(e.target.value)}
                                            placeholder="Enter regex pattern (e.g., \b\w+@\w+\.\w+\b)"
                                            className="flex-1 h-10 rounded-md border border-input px-3 text-sm"
                                        />
                                        <Button
                                            variant="outline"
                                            onClick={() => setCustomPattern('\\b\\w+@\\w+\\.\\w+\\b')}
                                        >
                                            Example
                                        </Button>
                                    </div>
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <div className="flex items-center space-x-2">
                                    <Switch id="make-unique" checked={makeUnique} onCheckedChange={setMakeUnique} />
                                    <Label htmlFor="make-unique">Remove duplicates</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Switch id="sort-results" checked={sortResults} onCheckedChange={setSortResults} />
                                    <Label htmlFor="sort-results">Sort results</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Switch id="include-empty" checked={!includeEmptyLines} onCheckedChange={(v) => setIncludeEmptyLines(!v)} />
                                    <Label htmlFor="include-empty">Remove empty lines</Label>
                                </div>
                            </div>
                            <Button onClick={extractText} disabled={!inputText.trim() || (extractionType === 'custom' && !customPattern)}>
                                <Search className="h-4 w-4 mr-2" /> Extract {extractionType}
                            </Button>
                        </TabsContent>
                    </Tabs>
                </CardContent>
            </Card>

            {/* About Tool */}
            <div className="mt-16 bg-muted/50 p-6 rounded-lg">
                <h3 className="text-lg font-medium mb-3">About This Tool</h3>
                <p className="mb-3">
                    The Text Extractor helps you find and extract specific types of content from your text.
                </p>
            </div>

            {/* SEO Content */}
            <div className="mt-16">
                <InformationalContent
                    title={textExtractorContent.title}
                    description={textExtractorContent.description}
                    content={textExtractorContent.content}
                    faqs={textExtractorContent.faqs}
                    relatedTools={textExtractorContent.relatedTools}
                />
            </div>
        </div>
    );
}